import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { failStatus, GenericRequest, successStatus } from '@core/models/request-status.model';
import { environment } from '@environments/environment';
import { Order, OrderListPagedResponse, OrderResponse } from '@b2b/features/order/utilities/order.model';
import { GetCartResponse } from '@b2b/features/cart/utilities/cart.model';
import { normalizeOrder } from '@b2b/features/order/utilities/order.normalizer';
import { CartConnector } from '@b2b/features/cart/core/cart.connector';
import { LocalStorageService } from '@shared/tools/local-storage.service';
import { CART_ID_KEY } from '@shared/tools/constants';

@Injectable({
  providedIn: 'root',
})
export class OrderConnector {
  constructor(
    private http: HttpClient,
    private translateService: TranslateService,
    private cartConnector: CartConnector,
    private localStorage: LocalStorageService,
  ) {}

  get(code: string): Observable<GenericRequest<Order[]>> {
    const url = `${environment.apiService}/order/${code}`;

    return this.http.get<GetCartResponse[]>(url).pipe(
      map(order => ({ ...successStatus, data: normalizeOrder(order) })),
      catchError(() => {
        return this.translateService.get('generic.requestError').pipe(
          map(message => ({
            ...failStatus,
            message,
          })),
        );
      }),
    );
  }

  list(b2bUnitUid: string, customerUid: string, page = 1): Observable<GenericRequest<OrderListPagedResponse>> {
    const url = `${environment.apiService}/order/list/${b2bUnitUid}/${customerUid}`;

    return this.http
      .get<{ items: OrderResponse[]; pagination: { currentPage: number; numberOfPages: number } }>(url, {
        params: { page: String(page) },
      })
      .pipe(
        map(response => {
          const data: OrderListPagedResponse = {
            items: response?.items ?? [],
            currentPage: response?.pagination?.currentPage ?? page,
            totalPages: response?.pagination?.numberOfPages ?? 1,
          };
          return { ...successStatus, data };
        }),
        catchError(() => {
          return this.translateService.get('generic.requestError').pipe(
            map(message => ({
              ...failStatus,
              message,
            })),
          );
        }),
      );
  }

  create(): Observable<GenericRequest<OrderResponse[]>> {
    const url = `${environment.apiService}/order/place-order`;

    return this.http.post<OrderResponse[]>(url, {}).pipe(
      switchMap(() => {
        this.localStorage.removeItem(CART_ID_KEY);
        return this.cartConnector.getOrCreate();
      }),
      map(() => ({ ...successStatus })),
      catchError(error => {
        return of({
          ...failStatus,
          message: error?.message || '',
        });
      }),
    );
  }
}
